import moment, { Moment } from 'moment';
import queryString from 'query-string';

import * as Api from '../../api/api';
import Job from '../../api/job-api-client';
import { AdditionalFiltersType } from '../applicant-export';

import { downloadBlob } from '../../util/download-blob';
import { error } from '../../widgets/error-message-dialog';

const getJobEeoExport =
  (jobId: number) =>
  (
    startDate: string,
    endDate: string,
    tz: string,
    filter?: string,
    chartType?: string,
    includeAutoScreened?: 0 | 1,
    additionalFilters: AdditionalFiltersType = {},
  ) =>
    new Job(jobId).getEeoExport(
      startDate,
      endDate,
      tz,
      filter,
      chartType,
      includeAutoScreened,
      additionalFilters,
    );

const getJobReportingExport =
  (jobId: number) =>
  (
    startDate: string,
    endDate: string,
    tz: string,
    filter?: string,
    chartType?: string,
    includeAutoScreened?: 0 | 1,
    additionalFilters: AdditionalFiltersType = {},
  ) =>
    new Job(jobId).getApplicantExport(
      startDate,
      endDate,
      tz,
      filter,
      chartType,
      includeAutoScreened,
      additionalFilters,
    );

const getOrgEeoExport =
  (orgId: number) =>
  (
    startDate: string,
    endDate: string,
    tz: string,
    filter?: string,
    chartType?: string,
    includeAutoScreened?: 0 | 1,
    additionalFilters: AdditionalFiltersType = {},
  ) =>
    Api.get(
      `/org/${orgId}/applicant-eeo-export?${queryString.stringify({
        startDate,
        endDate,
        tz,
        filter,
        chartType,
        includeAutoScreened,
        ...additionalFilters,
      })}`,
    );

const getOrgReportingExport =
  (orgId: number) =>
  (
    startDate: string,
    endDate: string,
    tz: string,
    filter?: string,
    chartType?: string,
    includeAutoScreened?: 0 | 1,
    additionalFilters: AdditionalFiltersType = {},
  ) =>
    Api.get(
      `/org/${orgId}/applicant-export?${queryString.stringify({
        startDate,
        endDate,
        tz,
        filter,
        chartType,
        includeAutoScreened,
        ...additionalFilters,
      })}`,
    );

const getRequestMethod = (
  orgId: number,
  jobId: number | null | undefined,
  exportType: 'eeo' | 'reporting',
) => {
  if (jobId) {
    return exportType === 'eeo' ? getJobEeoExport(jobId) : getJobReportingExport(jobId);
  }
  return exportType === 'eeo' ? getOrgEeoExport(orgId) : getOrgReportingExport(orgId);
};

export const exportApplicantData = async ({
  org,
  jobId,
  exportType,
  startDate,
  endDate,
  filter,
  chartType,
  includeAutoScreenedApplicants,
  additionalFilters,
}: {
  org: Org;
  jobId?: number | null;
  exportType: 'eeo' | 'reporting';
  startDate: Moment | string;
  endDate: Moment | string;
  filter?: string;
  chartType?: string;
  includeAutoScreenedApplicants?: boolean;
  additionalFilters?: AdditionalFiltersType;
}) => {
  let err = '';
  const TODAY = moment();
  const start = moment(startDate).format('YYYY-MM-DD');
  const end = moment(endDate).format('YYYY-MM-DD');
  const tz = moment().format('Z');
  const includeAutoScreened = includeAutoScreenedApplicants ? 1 : 0;
  const requestMethod = getRequestMethod(org.org_id, jobId, exportType);
  const res = await requestMethod(
    start,
    end,
    tz,
    filter,
    chartType,
    includeAutoScreened,
    additionalFilters || {},
  );

  if (!res?.ok) {
    error();
  } else if (res.headers.get('Content-type')?.includes('application/octet-stream')) {
    res.blob().then((blob) => {
      downloadBlob({
        blob,
        filename: `team_engine_eeo_export_${org.name}_${TODAY.format('MM_DD_YY')}.csv`,
      });
    });
  } else {
    const body = await res.json();
    if (body.errorMessage) {
      err = body.errorMessage;
    }
  }
  return { err };
};
